/**
 * Quick Schedule Popover
 *
 * Shown when clicking on an empty time slot.
 * Allows creating a new task with a pre-filled schedule.
 */

import { useState, useRef, useEffect } from 'react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { Plus, X, Clock } from 'lucide-react';
import { useClickOutside } from '@/hooks/useClickOutside';

interface QuickSchedulePopoverProps {
  isOpen: boolean;
  date: Date;
  startTimeMs: number;
  endTimeMs: number;
  onClose: () => void;
  onCreateTask: (name: string, scheduledStart: number, scheduledEnd: number) => void;
}

export function QuickSchedulePopover({
  isOpen,
  date,
  startTimeMs,
  endTimeMs,
  onClose,
  onCreateTask,
}: QuickSchedulePopoverProps) {
  const [name, setName] = useState('');
  const [clickOutsideEnabled, setClickOutsideEnabled] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Delay enabling click-outside by one frame so the opening pointerdown
  // event doesn't immediately close the popover.
  useClickOutside([ref], onClose, clickOutsideEnabled);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setClickOutsideEnabled(false);
      const raf = requestAnimationFrame(() => {
        setClickOutsideEnabled(true);
      });
      setTimeout(() => inputRef.current?.focus(), 50);
      return () => cancelAnimationFrame(raf);
    } else {
      setClickOutsideEnabled(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onCreateTask(name.trim(), startTimeMs, endTimeMs);
    setName('');
    onClose();
  };

  const startLabel = format(new Date(startTimeMs), 'HH:mm', { locale: it });
  const endLabel = format(new Date(endTimeMs), 'HH:mm', { locale: it });
  const dateLabel = format(date, 'd MMM', { locale: it });

  return (
    <div
      ref={ref}
      className="absolute z-50 w-64 bg-surface-primary rounded-xl border border-border-primary shadow-xl animate-scale-in"
      style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-border-primary">
        <div className="flex items-center gap-1.5 text-[11px] text-text-tertiary">
          <Clock className="w-3 h-3" />
          <span>{dateLabel} · {startLabel} - {endLabel}</span>
        </div>
        <button
          onClick={onClose}
          className="p-0.5 rounded hover:bg-surface-hover transition-colors"
        >
          <X className="w-3.5 h-3.5 text-text-quaternary" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-2">
        <input
          ref={inputRef}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nome task..."
          className="w-full px-2.5 py-1.5 text-[12px] rounded-lg bg-surface-secondary border border-border-primary focus:outline-none focus:border-accent-violet text-text-primary placeholder:text-text-quaternary"
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClose();
          }}
        />
        <button
          type="submit"
          disabled={!name.trim()}
          className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent-violet text-white text-[11px] font-medium hover:bg-accent-violet-light disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Plus className="w-3 h-3" />
          Crea task
        </button>
      </form>
    </div>
  );
}
